import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { IconAdjustmentsHorizontal, IconScale } from '@tabler/icons-react';
import { IconTile } from './primitives';

export const KORA_PRICE_PER_TICKET = 0.1;

const usd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

type Field = { key: string; label: string; value: number; set: (n: number) => void; min: number; max: number };

function Slider({ field, typed }: { field: Field; typed: boolean }) {
  const { key, label, value, set, min, max } = field;
  const top = Math.max(max, value);
  const pct = ((value - min) / (top - min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <label htmlFor={`svt-${key}`} className="text-sm" style={{ color: 'var(--warm-muted)' }}>{label}</label>
        {typed ? (
          <input
            type="number" min={0} inputMode="numeric" value={value}
            onChange={(e) => { const n = Number(e.target.value); set(Number.isFinite(n) && n >= 0 ? n : 0); }}
            aria-label={`${label} (exact value)`}
            className="w-24 rounded-full px-3 py-1 text-right font-semibold text-sm"
            style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}
          />
        ) : (
          <span className="rounded-full px-3 py-1 text-sm font-semibold tabular-nums" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>
            {value.toLocaleString()}
          </span>
        )}
      </div>
      <input
        id={`svt-${key}`} type="range" min={min} max={top} value={value} onChange={(e) => set(Number(e.target.value))}
        className="compare-slider w-full h-1.5 rounded-full cursor-pointer appearance-none"
        style={{ background: `linear-gradient(to right, var(--warm-text) ${pct}%, var(--warm-track) ${pct}%)` }}
      />
    </div>
  );
}

function CostBar({ label, value, share, tone }: { label: string; value: string; share: number; tone: 'muted' | 'bright' }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm mb-2">
        <span style={{ color: 'var(--warm-inv-muted)' }}>{label}</span>
        <span className="font-medium tabular-nums" style={{ color: 'var(--warm-inv-text)' }}>{value}</span>
      </div>
      <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(251,250,246,0.1)' }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.max(share * 100, 1.5)}%`,
            background: tone === 'bright' ? 'var(--warm-inv-text)' : 'rgba(251,250,246,0.38)',
            transition: 'width 500ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      </div>
    </div>
  );
}

/**
 * Per-seat vs pay-per-ticket calculator: one implementation for every page.
 * `full` adds the optional AI Analyst answers input and typed number fields.
 */
export function SeatsVsTicketsCalculator({ variant = 'compact' }: { variant?: 'compact' | 'full' }) {
  const full = variant === 'full';
  const [agents, setAgents] = useState(10);
  const [pricePerAgent, setPricePerAgent] = useState(50);
  const [tickets, setTickets] = useState(300);
  const [aiAnswers, setAiAnswers] = useState(0);

  const perSeat = agents * pricePerAgent;
  const kora = (tickets + (full ? aiAnswers : 0)) * KORA_PRICE_PER_TICKET;
  const saving = perSeat - kora;
  const saves = saving >= 0;
  const breakeven = Math.round(perSeat / KORA_PRICE_PER_TICKET);
  const scale = Math.max(perSeat, kora, 1);

  const fields: Field[] = [
    { key: 'agents', label: 'Agents', value: agents, set: setAgents, min: 1, max: 200 },
    { key: 'price', label: 'Price per agent / month (USD)', value: pricePerAgent, set: setPricePerAgent, min: 1, max: 300 },
    { key: 'tickets', label: 'Tickets per month', value: tickets, set: setTickets, min: 1, max: 10000 },
    ...(full ? [{ key: 'ai', label: 'AI Analyst answers per month (optional)', value: aiAnswers, set: setAiAnswers, min: 0, max: 2000 }] : []),
  ];

  return (
    <div className="warm-card p-3 sm:p-4" style={{ border: '1px solid var(--warm-border)', borderRadius: 28 }}>
      <div className="grid lg:grid-cols-12 gap-3">
        <div className="lg:col-span-7 rounded-[20px] p-6 sm:p-7 flex flex-col" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
          <div className="flex items-center gap-3 mb-7">
            <IconTile icon={IconAdjustmentsHorizontal} bg="var(--warm-card)" />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--warm-text)' }}>Your numbers</p>
              <p className="text-xs" style={{ color: 'var(--warm-muted)' }}>Drag the sliders to match your team</p>
            </div>
          </div>
          <div className="space-y-6 flex-1">
            {fields.map((f) => <Slider key={f.key} field={f} typed={full} />)}
          </div>
          <p className="mt-7 text-xs" style={{ color: 'var(--warm-muted)' }}>
            {full ? 'The $50 default is an example, not a quote from any vendor. Enter the price on your invoice.' : "Example only. Your vendor's price may differ."}
          </p>
          {!full && (
            <Link to="/helpdesk-cost-calculator" className="mt-3 text-sm font-medium inline-flex items-center gap-1 self-start" style={{ color: 'var(--warm-text)' }}>
              Open the full calculator <ArrowRight className="size-3.5" />
            </Link>
          )}
        </div>

        <div className="lg:col-span-5 relative overflow-hidden rounded-[20px] p-6 sm:p-7 flex flex-col" style={{ background: 'var(--warm-inv)', color: 'var(--warm-inv-text)', border: '1px solid var(--warm-border)' }} aria-live="polite">
          <div
            className="absolute -right-16 -top-20 w-64 h-64 rounded-full blur-3xl pointer-events-none"
            style={{ background: `radial-gradient(circle, ${saves ? 'rgba(110,190,130,0.35)' : 'rgba(231,160,90,0.35)'} 0%, transparent 70%)`, transition: 'background 500ms ease' }}
          />
          <p className="relative text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-inv-muted)' }}>
            {saves ? "You'd save" : 'Kora costs more'}
          </p>
          <p className="relative mt-3 flex items-baseline gap-2 flex-wrap">
            <span className="font-display font-light text-5xl sm:text-6xl leading-none tabular-nums" style={{ color: saves ? 'var(--warm-ok-on-inv)' : '#F2C27B' }}>
              {usd(Math.abs(saving))}
            </span>
            <span className="text-sm" style={{ color: 'var(--warm-inv-muted)' }}>/ month</span>
          </p>
          <p className="relative mt-2 text-sm" style={{ color: 'var(--warm-inv-muted)' }}>That's {usd(Math.abs(saving) * 12)} a year.</p>

          <div className="relative mt-8 space-y-5">
            <CostBar label="Per-seat cost" value={`${usd(perSeat)} / month`} share={perSeat / scale} tone="muted" />
            <CostBar label="Kora" value={`${usd(kora)} / month`} share={kora / scale} tone="bright" />
          </div>

          <div className="relative mt-8 pt-5 flex gap-3 text-sm leading-relaxed" style={{ borderTop: '1px solid rgba(251,250,246,0.14)', color: 'var(--warm-inv-muted)' }}>
            <IconScale size={18} stroke={1.5} className="shrink-0 mt-0.5" />
            <p>
              {perSeat > 0 ? (
                <>With these numbers, per-seat pricing only becomes cheaper above <strong style={{ color: 'var(--warm-inv-text)', fontWeight: 600 }}>{breakeven.toLocaleString('en-US')} tickets{full && ' (plus AI answers)'} a month</strong>.</>
              ) : 'Enter agents and a per-agent price to see your break-even volume.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
